import {useState} from "react";
import {Button, Dropdown, Menu, Modal, Select, Tooltip, Typography} from "antd";
import {DownOutlined} from "@ant-design/icons";
import Flex from "../../../core/components/Flex";
import {useDataGrid} from "../../../core/components/data-grid/DataGridContext";
import {useTranslation} from "react-i18next";
import ApiService from "../../../core/service/api-service";
import {PERMISSIONS, useUser} from "../../../core/context/UserContext";
import FileHelper from "../../../core/utils/file-helper";
import Colors from "../../../core/style/Colors";
import "../../../core/components/data-grid/grid-dropdown.css";
import {toReportGridQuery} from "./report-filter-query";
import {matchesSearch} from "../../../core/utils/text-search";

const colors = [
    {color: Colors.deletedRow, text: 'DELETED-ROW'},
    {color: Colors.confirmedRow, text: 'REPORT-CONFIRMED'},
    {color: Colors.notConfirmedRow, text: 'REPORT-NOT-CONFIRMED-ROW'},
    {color: Colors.selectedRow, text: 'SELECTED-ROW'},
]

export default function ReportGridTools()
{
    const {t} = useTranslation();
    const {selectedRows, queryModel, refreshDataGrid} = useDataGrid();
    const {hasPermission} = useUser();

    // İmza doktoru değiştirme: adaylar hastaneden bağımsız tüm doktorlar, modal açılınca çekilir.
    const [signUserModalVisible, setSignUserModalVisible] = useState(false)
    const [signUserCandidates, setSignUserCandidates] = useState([])
    const [signUserId, setSignUserId] = useState(null)
    const [savingSignUser, setSavingSignUser] = useState(false)

    const openSignUserModal = async () => {
        setSignUserId(null)
        setSignUserModalVisible(true)
        const response = await ApiService.Report.GetSignUserCandidates()
        if(response.success)
        {
            setSignUserCandidates(response.data)
        }
    }

    const saveSignUser = async () => {
        setSavingSignUser(true)
        try {
            const response = await ApiService.Report.SetSignUser(selectedRows.map(r => r.id), signUserId)
            if(response.success)
            {
                setSignUserModalVisible(false)
                await refreshDataGrid()
            }
        } finally {
            setSavingSignUser(false)
        }
    }

    const downloadExcel = async () => {
        // Grid/sayaç ile AYNI filtreden geçmek zorunda: ham queryModel gönderilirse tarihler
        // gün sınırına çekilmez ve Excel gridden farklı sayıda satır döner.
        const res = await ApiService.Report.GetReportsExcel(toReportGridQuery(queryModel))
        if(res.success)
        {

        }
    }

    return (
        <>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                <div>
                    <Flex flexDirection={'row'} alignItems={'center'}>
                        <div>
                            <Flex flexDirection={'row'}>
                                {
                                    colors.map(color => {
                                        return (
                                            <Tooltip title={t(color.text)}>
                                                <div style={{width: 16, height: 16, backgroundColor: color.color, marginRight: 4}} />
                                            </Tooltip>
                                        )
                                    })
                                }
                            </Flex>
                        </div>
                        <Dropdown
                            disabled={selectedRows.length === 0}
                            trigger={'click'}
                            overlayClassName={'grid-dropdown-overlay'}
                            overlay={(
                                <Menu>
                                    {
                                        hasPermission(PERMISSIONS.REPORT_DOWNLOAD_PDF) && (
                                            <Menu.Item
                                                key={'6'}
                                                onClick={async () => {
                                                    const response = await ApiService.ReportGenerator.CreatePdfZipByReportIdList(selectedRows.map(r => r.id));
                                                    if(response.success)
                                                    {
                                                        ApiService.downloadFile(response.data, response.fileName);
                                                    }
                                                }}
                                            >
                                                {t('Download PDF')}
                                            </Menu.Item>
                                        )
                                    }
                                    {
                                        hasPermission(PERMISSIONS.REPORT_DOWNLOAD_WORD) && (
                                            <Menu.Item
                                                key={'Download Word'}
                                                onClick={async () => {
                                                    const response = await ApiService.ReportGenerator.CreateWordZipByReportIdList(selectedRows.map(r => r.id));
                                                    if(response.success)
                                                    {
                                                        ApiService.downloadFile(response.data, response.fileName);
                                                    }
                                                }}
                                            >
                                                {t('Download Word')}
                                            </Menu.Item>
                                        )
                                    }
                                    {
                                        hasPermission(PERMISSIONS.REPORT_DOWNLOAD_SIGN) && (
                                            <Menu.Item
                                                key={'report-sign'}
                                                onClick={async () => {
                                                    const response = await ApiService.Report.CreateSignZip(selectedRows.map(r => r.id));
                                                    if(response.success)
                                                    {
                                                        ApiService.downloadFile(response.data, response.fileName);
                                                    }
                                                }}
                                            >
                                                {t('Download Sign')}
                                            </Menu.Item>
                                        )
                                    }
                                    {
                                        hasPermission(PERMISSIONS.REPORT_REMOVE_SIGN) && (
                                            <Menu.Item
                                                key={'report-remove-sign'}
                                                onClick={() => {
                                                    Modal.confirm({
                                                        title: t('Remove Signature'),
                                                        content: t('Signatures of the selected reports will be removed. Unsigned ones are skipped. Continue?'),
                                                        okText: t('OK'),
                                                        cancelText: t('Cancel'),
                                                        onOk: async () => {
                                                            const response = await ApiService.Report.RemoveSignature(selectedRows.map(r => r.id));
                                                            if(response.success)
                                                            {
                                                                await refreshDataGrid();
                                                            }
                                                        },
                                                    });
                                                }}
                                            >
                                                {t('Remove Signature')}
                                            </Menu.Item>
                                        )
                                    }
                                    {
                                        hasPermission(PERMISSIONS.REPORT_SIGN_USER_EDIT) && (
                                            <Menu.Item key={'report-change-sign-user'} onClick={openSignUserModal}>
                                                {t('Change Sign Doctor')}
                                            </Menu.Item>
                                        )
                                    }
                                </Menu>
                            )}
                        >
                            <Button
                                size={'small'}
                                type={'primary'}
                            >
                                {t('Operations')}<DownOutlined />
                            </Button>
                        </Dropdown>
                    </Flex>
                </div>
                <Button size={'small'} onClick={downloadExcel} color={'green'} variant={'solid'}>
                    Excel
                </Button>
            </div>
            <Modal
                title={t('Change Sign Doctor')}
                open={signUserModalVisible}
                onCancel={() => setSignUserModalVisible(false)}
                onOk={saveSignUser}
                okText={t('OK')}
                cancelText={t('Cancel')}
                okButtonProps={{disabled: !signUserId}}
                confirmLoading={savingSignUser}
                destroyOnClose
            >
                <Typography.Paragraph>
                    {t('The sign doctor of the selected confirmed reports will be changed. Report outputs are regenerated and the report is sent to HBYS again. Unconfirmed reports are skipped.')}
                </Typography.Paragraph>
                <Select
                    showSearch
                    style={{width: '100%'}}
                    placeholder={t('Select Doctor')}
                    value={signUserId}
                    onChange={setSignUserId}
                    options={signUserCandidates}
                    fieldNames={{label: 'visibleName', value: 'id'}}
                    filterOption={(input, option) => matchesSearch(option?.visibleName, input)}
                />
            </Modal>
        </>
    )
}
